import React from 'react';
import PropTypes from 'prop-types';

/**
 * GridLayout Component
 * 
 * A responsive grid layout for rendering widgets with configurable dimensions.
 * Uses absolute positioning to match the exact layout from the preview.
 */
// eslint-disable-next-line no-unused-vars
const GridLayout = ({ questionGroups, page, validationErrors, children }) => {
  if (!questionGroups || questionGroups.length === 0) {
    return null;
  }

  // Base scale factor - how many pixels per grid unit
  const baseHeightUnit = 40; // pixels per y unit
  const baseWidthUnit = 8.33; // percentage per x unit (100% / 12 columns)

  let maxContainerHeight = 0;
  // Calculate the maximum height required by the widgets
  questionGroups.forEach(questionGroup => {
    const y = questionGroup.attributes?.y || 0;
    const height = questionGroup.attributes?.height || 2; // Default height is 2 units
    const elementBottom = (y + height) * baseHeightUnit;
    if (elementBottom > maxContainerHeight) {
      maxContainerHeight = elementBottom;
    }
  });

  // Ensure a minimum height if no elements or all elements have zero height,
  // though the initial check for empty questionGroups should prevent fully zero height.
  // If maxContainerHeight is 0 and there are items, it implies items might have 0 height,
  // which might be valid. Or provide a fallback small height like baseHeightUnit if 0 is undesirable.
  // For now, using the calculated maxContainerHeight directly.

  return (
    <div className="grid-layout">
      <div className="container-fluid p-0 position-relative" style={{ height: `${maxContainerHeight}px` }}>
        {questionGroups.map((questionGroup, index) => {
          // Get position and dimensions
          const x = questionGroup.attributes?.x || 0;
          const y = questionGroup.attributes?.y || 0;
          const width = questionGroup.attributes?.width || 12;
          const height = questionGroup.attributes?.height || 2;
          
          // Calculate position and dimensions as percentages/pixels
          const leftPosition = x * baseWidthUnit;
          const topPosition = y * baseHeightUnit;
          const widthValue = width * baseWidthUnit;
          const heightValue = height * baseHeightUnit;
          
          // Create a unique key for the widget
          const key = `${questionGroup.key || 'widget'}-${index}`;
          
          return (
            <div 
              key={key}
              style={{ 
                position: 'absolute',
                left: `${leftPosition}%`,
                top: `${topPosition}px`,
                width: `${widthValue}%`,
                height: `${heightValue}px`,
                padding: '0 15px'
              }}
            >
              {children(questionGroup, index)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

GridLayout.propTypes = {
  questionGroups: PropTypes.array.isRequired,
  page: PropTypes.object.isRequired,
  validationErrors: PropTypes.array,
  children: PropTypes.func.isRequired
};

export default GridLayout; 